import { Component, computed, inject, signal, TemplateRef, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { CampaignType, StrategyAsin } from "@front/m19-api-client";
import { StrategyService } from "@front/m19-injection-token";
import { AccountSelectionService, AsinService, MapPipe, TacosStrategiesService } from "@front/m19-services";
import {
  ConfirmPopupComponent,
  ConfirmPopupData,
  IAlertComponent,
  IButtonDirective,
  ITooltipDirective,
  ModalOptions,
  ModalRef,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { LowInventoryWarningComponent } from "@m19-board/inventory/inventory-rules/low-inventory-warning.component";
import { AsinSelectionWrapperComponent } from "@m19-board/shared/asin-selection/asin-selection-wrapper.component";
import { AsinSelectionComponent } from "@m19-board/shared/asin-selection/asin-selection.component";
import {
  StrategyGroupMoveProductModalComponent,
  StrategyGroupMoveProductModalData,
} from "@m19-board/sponsored-product/sp-page/move-product/strategy-group-move-product-modal.component";
import { LimitWarningComponent } from "@m19-board/strategies/limit-warning/limit-warning.component";
import {
  MoveStrategyAsinsModalComponent,
  MoveStrategyAsinsModalData,
} from "@m19-board/strategies/strategy-asins/move-strategy-asins-modal.component";
import { StrategyPageService } from "@m19-board/strategies/strategy-page/strategy-page.service";
import { finalize, Observable, of, take } from "rxjs";
import { switchMap } from "rxjs/operators";

@Component({
  selector: "app-strategy-asins",
  imports: [
    AsinSelectionComponent,
    LowInventoryWarningComponent,
    TranslocoDirective,
    MapPipe,
    LimitWarningComponent,
    IButtonDirective,
    AsinSelectionWrapperComponent,
    ITooltipDirective,
    IAlertComponent,
  ],
  templateUrl: "./strategy-asins.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyAsinsComponent {
  private readonly strategyPageService = inject<StrategyPageService<CampaignType>>(StrategyPageService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly asinService = inject(AsinService);
  private readonly modalService = inject(ModalService);
  private readonly toasterService = inject(ToasterService);
  private readonly strategyService = inject(StrategyService);
  private readonly translationService = inject(TranslocoService);
  private readonly tacosStrategiesService = inject(TacosStrategiesService);
  private readonly router = inject(Router);

  readonly CampaignType = CampaignType;

  ASINS_LIMIT = this.strategyService.maxAsinsPerStrategy;

  strategy = this.strategyPageService.strategy;
  strategyGroup = this.strategyPageService.strategyGroup;

  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, {
    initialValue: false,
  });

  catalog = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.asinService.getCatalog(am.accountId, am.marketplace)),
    ),
  );
  asinEligibility = computed(() => this.catalog()?.getSPEligibility());

  disabledAsinsToAdd = toSignal(this.tacosStrategiesService.unavailableAsinsForSponsoredAds$, {
    initialValue: new Set<string>(),
  });
  asinsList = computed<string[]>(() => this.strategy()?.asins.map((a) => a.asin) ?? []);

  ineligibleAsins = computed<string[]>(() => {
    const asinEligibility = this.asinEligibility();
    if (!asinEligibility) return [];
    return this.asinsList().filter((asin) => !asinEligibility.has(asin) || asinEligibility.get(asin)?.status === false);
  });
  brands = computed<Set<string>>(() => {
    const catalog = this.catalog();
    if (!catalog) return new Set();

    return this.asinsList().reduce((brands, item) => {
      const brand = catalog.asinOffers.get(item)?.brand;
      if (brand) brands.add(brand);
      return brands;
    }, new Set<string>());
  });

  @ViewChild("productAdd") productAdd!: TemplateRef<any>;
  productModalRef?: ModalRef<any>;

  selectedAsins = signal<string[]>([]);
  newEditList = signal<string[]>([]);
  isUpdatingAsins = signal(false);

  getAsins(strategyAsin: StrategyAsin): string {
    return strategyAsin.asin!;
  }

  getBrands(): string {
    return Array.from(this.brands()).join(" - ");
  }

  toggleProductAdd() {
    this.productModalRef = this.modalService.openModal(this.productAdd, {
      modalTitle: this.translationService.translate("sd-page.add_products"),
      size: "xl",
      height: "fill",
    });
  }

  moveStrategyAsins(): void {
    const toMove = this.selectedAsins();
    const strategy = this.strategy()!;
    if (toMove.length === 0) {
      return;
    }
    if (toMove.length === this.asinsList().length) {
      this.toasterService.error("You cannot move all ASINs from a strategy");
      return;
    }

    const modalOptions: ModalOptions<MoveStrategyAsinsModalData | StrategyGroupMoveProductModalData> = {
      data: {
        source: strategy,
        asinsToMove: toMove,
        strategyGroup: this.strategyGroup(),
      },
      modalTitle: this.translationService.translate("move-strategy-asins-modal.move_asins_to_strategy"),
    };
    if (this.strategyGroup()) {
      this.modalService
        .openModal(StrategyGroupMoveProductModalComponent, modalOptions)
        .afterClosed()
        .subscribe(() => {
          this.router.navigate(
            ["/advertising/sponsored-product/strategy-group/" + this.strategyGroup()!.strategyGroupId],
            {
              queryParamsHandling: "merge",
            },
          );
        });
    } else {
      this.modalService.openModal(MoveStrategyAsinsModalComponent, modalOptions);
    }
  }

  updateStrategyAsinsFromModal() {
    const newAsinList = this.newEditList();
    if (!this.strategy() || newAsinList.length === 0) {
      return;
    }

    this.doUpdateStrategyAsins(this.newEditList());
  }

  deleteAsinsFromStrategy(asinsToDelete: string[]) {
    this.doUpdateStrategyAsins(this.asinsList().filter((asin) => !asinsToDelete.includes(asin)));
  }

  private doUpdateStrategyAsins(newAsinsList: string[]) {
    this.isUpdatingAsins.set(true);

    const asinsToDelete = this.asinsList().filter((a) => !newAsinsList.includes(a));
    const maybeAskConfirmation$ =
      asinsToDelete.length > 0 ? this.askForDeleteConfirmation(asinsToDelete) : of(undefined);

    maybeAskConfirmation$
      .pipe(
        switchMap(() => this.strategyService.updateStrategyAsins(this.strategy()!, newAsinsList)),
        take(1),
        finalize(() => this.isUpdatingAsins.set(false)),
      )
      .subscribe({
        next: () => {
          this.productModalRef?.close();
          this.selectedAsins.set([]);
          this.toasterService.success(this.translationService.translate("strategy-asins.asins_successfully_updated"));
        },
        error: () => {},
      });
  }

  private askForDeleteConfirmation(asinsToDelete: string[]): Observable<void> {
    return this.modalService
      .openModal<ConfirmPopupData, void>(ConfirmPopupComponent, {
        modalTitle: this.translationService.translate("sp-strategy-group-page.product_deletion"),
        data: {
          message: this.translationService.translate("strategy-asins.confirm_product_deletion", {
            count: asinsToDelete.length,
          }),
          confirmLabel: this.translationService.translate("common.delete"),
          color: "red",
        },
      })
      .afterClosed();
  }

  private prettyPrintAsinList(asins: string[]) {
    const maxAsins = 5;
    let message = asins.slice(0, maxAsins).join(", ");
    if (asins.length > maxAsins) {
      message += "…";
    }
    return message;
  }
}
